import React from "react";
import { Box, Typography, Link } from "@mui/material";

const EFFECTIVE_DATE = "July 1, 2026";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box mb={4}>
    <Typography variant="h5" component="h2" gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Box maxWidth="900px" margin="0 auto" padding="16px">
      <Typography variant="h4" component="h1" gutterBottom>
        Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Effective date: {EFFECTIVE_DATE}
      </Typography>

      <Typography variant="body1" paragraph>
        Family Tree &amp; Me ("we," "us," or "our") takes the privacy of your
        family's information seriously. This policy explains what we collect,
        why we collect it, and the choices you have. We are located in Orem,
        Utah, and can be reached at{" "}
        <Link href="mailto:shirlenedymock@mac.com">shirlenedymock@mac.com</Link>.
      </Typography>

      <Section title="1. Information We Collect">
        <Typography variant="body1" component="ul" sx={{ pl: 3 }} paragraph>
          <li>
            <strong>Account information</strong> — your name, email address,
            and password (stored securely by Firebase Authentication; we never
            see your password).
          </li>
          <li>
            <strong>Family information you provide</strong> — the names, birth
            dates, death dates, relationships, and photographs you enter or
            upload to build your family tree and complete order forms. This
            may include information about your living relatives.
          </li>
          <li>
            <strong>Order information</strong> — the products you purchase,
            order status, and payment confirmation details from PayPal. We do
            not receive or store your credit card number.
          </li>
          <li>
            <strong>Messages</strong> — contact form submissions and emails
            you send us.
          </li>
          <li>
            <strong>Technical information</strong> — if the site encounters an
            error while you use it, we record the error message, the page
            address, and your browser type so we can fix the problem.
          </li>
        </Typography>
      </Section>

      <Section title="2. How We Use Your Information">
        <Typography variant="body1" component="ul" sx={{ pl: 3 }} paragraph>
          <li>To design, produce, and deliver the artwork you order.</li>
          <li>To manage your account, orders, and customer support.</li>
          <li>
            To send transactional emails such as order confirmations and
            status updates.
          </li>
          <li>To keep the site secure and fix technical problems.</li>
        </Typography>
        <Typography variant="body1" paragraph>
          We do <strong>not</strong> sell your personal information, and we do
          not use your photographs or family information for advertising
          without your written permission.
        </Typography>
      </Section>

      <Section title="3. Who We Share It With">
        <Typography variant="body1" paragraph>
          We share information only with the service providers that make the
          Service work:
        </Typography>
        <Typography variant="body1" component="ul" sx={{ pl: 3 }} paragraph>
          <li>
            <strong>Google Firebase</strong> — hosting, authentication,
            database, and photo storage.
          </li>
          <li>
            <strong>PayPal</strong> — payment processing. Your payment details
            go directly to PayPal under{" "}
            <Link
              href="https://www.paypal.com/us/legalhub/privacy-full"
              target="_blank"
              rel="noopener noreferrer"
            >
              PayPal's privacy policy
            </Link>
            .
          </li>
          <li>
            <strong>Twilio SendGrid</strong> — delivery of transactional
            emails.
          </li>
        </Typography>
        <Typography variant="body1" paragraph>
          We may also disclose information if required by law or to protect
          our legal rights.
        </Typography>
      </Section>

      <Section title="4. Photographs and Living Relatives">
        <Typography variant="body1" paragraph>
          The photographs and family details you upload often include other
          people, some of them living. Please make sure you have the right to
          share this information. We use it only to create your artwork, and
          access is restricted to your account and the staff producing your
          order.
        </Typography>
      </Section>

      <Section title="5. Data Retention and Deletion">
        <Typography variant="body1" paragraph>
          We keep your account data, family tree, and uploaded photos while
          your account is active so you can reorder and update your tree.
          Order records are retained for our accounting and legal obligations.
          You may request deletion of your account and associated data at any
          time by emailing{" "}
          <Link href="mailto:shirlenedymock@mac.com">
            shirlenedymock@mac.com
          </Link>
          ; we will delete data that we are not legally required to keep.
        </Typography>
      </Section>

      <Section title="6. Security">
        <Typography variant="body1" paragraph>
          Your data is stored on Google Firebase infrastructure with
          encryption in transit and at rest. Access rules restrict your family
          tree, photos, and orders to your account and authorized
          administrators. No system is perfectly secure, but we follow
          industry-standard practices to protect your information.
        </Typography>
      </Section>

      <Section title="7. Children's Privacy">
        <Typography variant="body1" paragraph>
          The Service is intended for adults. We do not knowingly collect
          personal information directly from children under 13. Family trees
          may include children's names and photos that a parent, grandparent,
          or guardian chooses to upload — that content is controlled by the
          account holder.
        </Typography>
      </Section>

      <Section title="8. Your Choices and Rights">
        <Typography variant="body1" component="ul" sx={{ pl: 3 }} paragraph>
          <li>
            You can view and edit your family tree, photos, and account
            details from your dashboard at any time.
          </li>
          <li>You can request a copy or deletion of your data by email.</li>
          <li>
            Utah residents may have additional rights under the Utah Consumer
            Privacy Act; contact us to exercise them.
          </li>
        </Typography>
      </Section>

      <Section title="9. Changes to This Policy">
        <Typography variant="body1" paragraph>
          We may update this policy from time to time. The effective date at
          the top shows the latest revision. Material changes will be
          communicated through the Service or by email.
        </Typography>
      </Section>

      <Section title="10. Contact">
        <Typography variant="body1" paragraph>
          Questions or requests about your privacy? Email{" "}
          <Link href="mailto:shirlenedymock@mac.com">
            shirlenedymock@mac.com
          </Link>{" "}
          or call 801-400-5802.
        </Typography>
      </Section>
    </Box>
  );
};

export default PrivacyPolicyPage;
